// src/components/Layout.js
import React, { useState, useLayoutEffect, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import { countries } from '../utils/countries'; // Ensure this path is correct
import { safeLocalStorage } from '../utils/ssrSafe';
import NavBar from './NavBar';
import Footer from './Footer';
import './Layout.css';
import '../Theme.css';

// Lazy load CookieConsentBanner to improve initial page load performance
const CookieConsentBanner = lazy(() => import('./CookieConsentBanner'));

// Store the computed padding once in this module-level variable.
let computedPaddingTop = null;
const DEFAULT_COUNTRY = 'canada';

function Layout() {
  const { countryCode: paramCountryCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial country directly to avoid loading state for main render
  let initialCountry = DEFAULT_COUNTRY;
  if (paramCountryCode && countries.find(c => c.code === paramCountryCode)) {
    initialCountry = paramCountryCode;
  }

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  // Removed isLoadingLocation state

  const [multiCountries, setMultiCountries] = useState(() => {
    const saved = safeLocalStorage.getItem('multiCountries');
    return saved ? JSON.parse(saved) : [];
  });

  const [paddingTop, setPaddingTop] = useState('0px');
  const offset = 200; // Keep your existing offset

  // Effect to handle country logic, root path redirect, and invalid country codes
  useEffect(() => {
    const currentPath = location.pathname;
    // paramCountryCode is from the URL, selectedCountry is the current React state
    
    let targetPath = null;
    let countryToSet = selectedCountry;

    if (currentPath === '/') {
      targetPath = `/country/${DEFAULT_COUNTRY}/home`;
      countryToSet = DEFAULT_COUNTRY; // Ensure selectedCountry aligns if redirecting to default
    } else if (paramCountryCode) {
      if (!countries.find(c => c.code === paramCountryCode)) {
        console.warn(`Invalid country code in URL (${paramCountryCode}), redirecting.`);
        const page = currentPath.split('/').pop() || 'home';
        targetPath = `/country/${DEFAULT_COUNTRY}/${page}`;
        countryToSet = DEFAULT_COUNTRY;
      } else {
        // Valid country code from URL
        countryToSet = paramCountryCode;
      }
    }
    // If no paramCountryCode, selectedCountry remains as initialized (or from previous state)
    // which should be 'canada' or a valid one from a previous direct URL hit.

    if (selectedCountry !== countryToSet) {
      setSelectedCountry(countryToSet);
    }

    if (targetPath && targetPath !== currentPath) { // Navigate only if target is different
      navigate(targetPath, { replace: true });
    }
  }, [location, paramCountryCode, navigate, selectedCountry]);


  // --- Keep other existing effects ---
  useEffect(() => {
    // Update localStorage for multiCountries when it changes
    safeLocalStorage.setItem('multiCountries', JSON.stringify(multiCountries));
  }, [multiCountries]);

  useLayoutEffect(() => {
    // Only run on client-side where DOM is available
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    
    // Recalculate paddingTop
    const navbarElement = document.querySelector('.navbar');
    if (navbarElement) {
      const height = navbarElement.getBoundingClientRect().height;
      computedPaddingTop = Math.max(height - offset, 0);
    } else {
      computedPaddingTop = 0;
    }
    setPaddingTop(`${computedPaddingTop}px`);
  }, [location.pathname]); // Re-run if path changes, in case NavBar height changes

  useEffect(() => {
    // Disabled: Scroll to top functionality moved to dedicated ScrollToTop component
    // This was causing conflicts with the main scroll-to-top logic
    // if (typeof window !== 'undefined') {
    //   window.scrollTo({ top: 0, behavior: 'auto' });
    // }
  }, [location.pathname]);

  // --- Keep Handlers ---
  const handlePrimarySelect = useCallback((code) => {
    setSelectedCountry(code);
    safeLocalStorage.setItem('selectedCountry', code);
    setMultiCountries([]); // Reset multi-countries when primary changes
    safeLocalStorage.removeItem('multiCountries'); // Clear from storage too
    navigate(`/country/${code}/home`); // Navigate to home of new primary country
  }, [navigate]); // Added navigate to dependency array

  const handleMultiCheckboxChange = useCallback((code, isChecked) => {
    setMultiCountries(prev =>
      isChecked ? [...prev, code] : prev.filter(c => c !== code)
    );
  }, []); // Empty dependency array as setMultiCountries is stable

  // --- Prepare Context ---
  // Memoize the context object to prevent unnecessary re-renders in consumers
  const outletContextValue = useMemo(() => ({
    countryCode: selectedCountry,
    selectedCountries: multiCountries,
    handlePrimarySelect,
    handleMultiCheckboxChange,
    countries,
  }), [selectedCountry, multiCountries, handlePrimarySelect, handleMultiCheckboxChange]); // countries is stable as it's an import

  // Determine if the current page is the Home page to preload hero image and for padding logic
  const isHomePage = location.pathname.endsWith('/home') || location.pathname.endsWith('/');

  // Always use the Canada theme class
  const themeClass = `theme-${selectedCountry || DEFAULT_COUNTRY}`;

  // ensure theme-<country> is also set on <html> so CSS vars load immediately on refresh
  useEffect(() => {
    const html = document.documentElement;
    // remove any known theme classes
    html.classList.remove('theme-usa','theme-canada','theme-india','theme-uk');
    html.classList.add(`theme-${selectedCountry || DEFAULT_COUNTRY}`);
  }, [selectedCountry]);

  // Determine padding based on location
  const isProductListPage = location.pathname.includes('/products'); // More robust check
  const applySpecialPadding = !isProductListPage && !isHomePage;

  return (
    <div className={`layout-container ${themeClass}`}>
      <Helmet>
        {/* Preload main CSS chunk - ensure the path is correct based on your build output */}
        <link rel="preload" href="/static/css/main.chunk.css" as="style" fetchpriority="high" />
        {/* Conditionally preload Hero Canada AVIF image only on the home page */}
        {isHomePage && (
          <link rel="preload" href="/Hero_canada.avif" as="image" type="image/avif" fetchpriority="high" />
        )}
      </Helmet>
      <NavBar selectedCountry={selectedCountry} />
      <main className={`main-content ${applySpecialPadding ? "special-padding" : ""}`} style={applySpecialPadding ? { paddingTop } : {}}>
        <Outlet context={outletContextValue} />
      </main>
      <Footer countryCode={selectedCountry} />
      {/* Lazy load cookie banner with suspense to prevent blocking initial render */}
      <Suspense fallback={null}>
        <CookieConsentBanner countryCode={selectedCountry} />
      </Suspense>
    </div>
  );
}

export default Layout;
